import { createApp, markRaw } from 'vue'
import { createHead } from '@unhead/vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { SplashScreen } from '@capacitor/splash-screen'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { App as CapacitorApp } from '@capacitor/app'

import App from '@/App'
import router from '@/router'
import { supabase } from '@/supabase.js'

import * as Sentry from '@sentry/vue'

import {
  IonicVue,
  IonPage,
  IonSearchbar,
  IonContent,
  IonHeader,
  IonList,
  IonRouterOutlet,
  IonLabel,
  IonItem,
  IonToolbar,
  IonBackButton,
  IonSegment,
  IonSegmentButton,
  IonInput,
  IonButton,
  IonButtons,
  IonTitle,
  IonModal,
  IonMenuToggle,
  IonMenu,
  IonRefresher,
  IonRefresherContent
} from '@ionic/vue'

// FontAwesome Icons
/* import the fontawesome core */
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import icons from './bootstrap/font-awesome.js'
// FontAwesome Icons

// defineCustomElements(window);
import './style.css'

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css'

/* Basic CSS for apps built with Ionic */
// import "@ionic/vue/css/normalize.css";
// import "@ionic/vue/css/structure.css";
// import "@ionic/vue/css/typography.css";

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css'
// import "@ionic/vue/css/float-elements.css";
// import "@ionic/vue/css/text-alignment.css";
// import "@ionic/vue/css/text-transformation.css";
// import "@ionic/vue/css/flex-utils.css";
// import "@ionic/vue/css/display.css";
// import { defineCustomElements } from '@ionic/pwa-elements/loader';

/* Theme variables */
import './theme/variables.css'
/* iOS 26 Liquid Glass Theme */
import './theme/ios26.css'
import './bootstrap/index.js'
import vResetScroll from './directives/vResetScroll'

// Capgo OTA Updates
// Set VITE_CAPGO_DISABLED=true to completely disable Capgo for local testing
// Note: autoUpdate is disabled in capacitor.config.json, we enable it here
const capgoDisabled = import.meta.env.VITE_CAPGO_DISABLED === 'true'
const appMode = import.meta.env.VITE_APP_MODE

// Channel preference is stored entirely client-side in localStorage
const CHANNEL_STORAGE_KEY = 'spectra_update_channel'

if (capgoDisabled) {
  console.log('[Capgo] DISABLED - using built-in bundle only')
} else if (appMode === 'development') {
  // Development mode: ensure autoUpdate stays disabled, use built-in bundle
  console.log(
    '[Capgo] Development mode: using built-in bundle, autoUpdate disabled'
  )
} else if (appMode === 'iostest' || appMode === 'production') {
  // Enable OTA updates
  CapacitorUpdater.notifyAppReady()

  // Initialize channel preference in localStorage if not already set
  try {
    const storedChannel = localStorage.getItem(CHANNEL_STORAGE_KEY)
    if (!storedChannel) {
      // No channel set yet, use default based on mode
      // iostest builds default to beta, production builds default to production
      const defaultChannel = appMode === 'iostest' ? 'beta' : 'production'
      localStorage.setItem(CHANNEL_STORAGE_KEY, defaultChannel)
      console.log(
        `[Capgo] ${appMode} mode: Set default channel to ${defaultChannel}`
      )
    } else {
      console.log(
        `[Capgo] ${appMode} mode: Using persisted channel: ${storedChannel}`
      )
    }
  } catch (err) {
    console.log('[Capgo] Could not access localStorage:', err.message)
  }

  console.log(`[Capgo] ${appMode} mode: OTA updates enabled`)
}

// Cleanup old Capgo bundles after app is ready
// This runs asynchronously and doesn't block app startup
import { useCapgoCleanup } from '@/composables/useCapgoCleanup'
const { cleanupOldBundles, cleanupOrphanedFiles } = useCapgoCleanup()

// Delay cleanup to ensure app is fully initialized (skip if Capgo disabled)
setTimeout(async () => {
  if (Capacitor.isNativePlatform() && !capgoDisabled) {
    try {
      console.log('[Main] Starting Capgo bundle cleanup...')
      const bundleResult = await cleanupOldBundles()
      console.log('[Main] Bundle cleanup result:', bundleResult)

      // Also clean up orphaned files
      const orphanResult = await cleanupOrphanedFiles()
      console.log('[Main] Orphan cleanup result:', orphanResult)
    } catch (error) {
      console.error('[Main] Error during Capgo cleanup:', error)
    }
  }
}, 5000) // Wait 5 seconds after app starts

library.add({ ...icons })

const app = createApp(App)
const head = createHead()

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
pinia.use(({ store }) => {
  store.router = markRaw(router)
})

if (import.meta.env.VITE_APP_MODE === 'production') {
  Sentry.init({
    app,
    dsn: 'https://1397816e4235ebee1c17124a7c3d99ce@o4508857362874368.ingest.us.sentry.io/4509039565471744',
    integrations: [],
    tracesSampleRate: 1.0
  })

  // Initialize PostHog Analytics
  import('./composables/usePostHog.js').then(({ usePostHog }) => {
    const { init } = usePostHog()
    init()
  })
}

app.use(IonicVue)
app.use(head)
app.use(pinia)
app.use(router)

// Eagerly initialize stores to prevent re-hydration on lazy-loaded route chunks
// This ensures stores are hydrated once at startup and shared across all components
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreLists } from '@/stores/storeLists'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreReviews } from '@/stores/storeReviews'
import { useStoreFeed } from '@/stores/storeFeed'
import { useStoreHomeFeed } from '@/stores/storeHomeFeed'

// Touch the stores to trigger hydration before any route components load
const _storeAuth = useStoreAuth()
const _storeLists = useStoreLists()
const _storeProfile = useStoreProfile()
const _storeReviews = useStoreReviews()
const _storeFeed = useStoreFeed()
const _storeHomeFeed = useStoreHomeFeed()

app.component('IonContent', IonContent)
app.component('IonPage', IonPage)
app.component('IonHeader', IonHeader)
app.component('IonList', IonList)
app.component('IonRouterOutlet', IonRouterOutlet)
app.component('IonLabel', IonLabel)
app.component('IonItem', IonItem)
app.component('IonSearchbar', IonSearchbar)
app.component('IonToolbar', IonToolbar)
app.component('IonBackButton', IonBackButton)
app.component('IonSegment', IonSegment)
app.component('IonSegmentButton', IonSegmentButton)
app.component('IonInput', IonInput)
app.component('IonButton', IonButton)
app.component('IonButtons', IonButtons)
app.component('IonTitle', IonTitle)
app.component('IonModal', IonModal)
app.component('IonMenuToggle', IonMenuToggle)
app.component('IonMenu', IonMenu)
app.component('IonRefresher', IonRefresher)
app.component('IonRefresherContent', IonRefresherContent)

app.component('FaIcon', FontAwesomeIcon)
app.directive('reset-scroll', vResetScroll)

// Initialize StatusBar for iOS to ensure it's transparent
const setupStatusBar = async () => {
  try {
    if (Capacitor.getPlatform() === 'ios') {
      await StatusBar.setOverlaysWebView({ overlay: true })
      await StatusBar.setStyle({ style: Style.Dark })
      await StatusBar.setBackgroundColor({ color: '#00000000' })
    }
  } catch (err) {
    console.error('Error configuring status bar', err)
  }
}

// Initialize deeplink handling
const initDeeplinkListener = () => {
  if (Capacitor.isNativePlatform()) {
    CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
      try {
        // Handle auth session handoff from web magic link flow
        if (url?.includes('auth/handoff')) {
          const urlObj = new URL(url)
          const code = urlObj.searchParams.get('code')
          const redirect = urlObj.searchParams.get('redirect') || '/'

          if (code) {
            // Exchange transfer code for session tokens via edge function
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
            const res = await fetch(
              `${supabaseUrl}/functions/v1/auth-transfer?code=${encodeURIComponent(code)}`
            )

            if (res.ok) {
              const { access_token, refresh_token } = await res.json()

              if (access_token && refresh_token) {
                const { data, error } = await supabase.auth.setSession({
                  access_token,
                  refresh_token
                })

                if (!error && data.session) {
                  const storeAuth = useStoreAuth()
                  storeAuth.handleSuccessfulLogin(data.session)
                  router.push(redirect).catch(() => {})
                }
              }
            }
          }
          return
        }

        // Handle spectra.reviews URLs
        if (url?.includes('spectra.reviews')) {
          const urlObj = new URL(url)
          const path = urlObj.pathname

          // Navigate to the path using Vue Router
          if (path && path !== '/') {
            router.push(path).catch((error) => {
              console.error('Navigation error:', error)
            })
          }
        }
      } catch (error) {
        console.error('Error handling deeplink:', error)
      }
    })
  }
}

// Initialize app lifecycle listeners (pause/resume handling)
const initAppLifecycleListeners = () => {
  if (Capacitor.isNativePlatform()) {
    // Track when app was last active to determine if refresh is needed
    let lastActiveTime = Date.now()
    const REFRESH_THRESHOLD_MS = 30000 // 30 seconds - refresh if backgrounded longer

    CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        // App resumed from background
        const timeInBackground = Date.now() - lastActiveTime
        console.log(
          `[App Lifecycle] App resumed after ${Math.round(timeInBackground / 1000)}s in background`
        )

        // Only refresh if app was backgrounded for longer than threshold
        if (timeInBackground > REFRESH_THRESHOLD_MS) {
          console.log('[App Lifecycle] Triggering app refresh after background')
          // Dispatch custom event for App.vue to handle the refresh
          window.dispatchEvent(new CustomEvent('app-resumed-from-background'))
        }
      } else {
        // App going to background
        lastActiveTime = Date.now()
        console.log('[App Lifecycle] App going to background')
      }
    })
  }
}

// Initialize Capgo Updater
const initCapgoUpdater = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      // Listen for update download events
      CapacitorUpdater.addListener('download', async (info) => {
        console.log('Capgo update downloading', info)
        // Check if download completed by checking bundle status
        // The download event may fire multiple times during download
        // We'll check bundle status after a delay to see if it's ready
        setTimeout(async () => {
          try {
            const bundlesResult = await CapacitorUpdater.list()
            const bundlesArray = Array.isArray(bundlesResult)
              ? bundlesResult
              : bundlesResult?.bundles || []

            const readyBundle = bundlesArray.find(
              (b) => b.status === 'downloaded' || b.status === 'pending'
            )

            if (readyBundle) {
              console.log('Capgo update downloaded and ready:', readyBundle)
              // Emit custom event for components to listen to
              window.dispatchEvent(
                new CustomEvent('capgo-update-downloaded', {
                  detail: readyBundle
                })
              )
            }
          } catch (err) {
            console.error('Error checking bundle status after download:', err)
          }
        }, 2000) // Check after 2 seconds to allow bundle status to update
      })

      // Listen for update install events
      CapacitorUpdater.addListener('updateFailed', (info) => {
        console.error('Capgo update failed', info)
      })

      // Listen for update available events
      CapacitorUpdater.addListener('updateAvailable', async (info) => {
        console.log('Capgo update available', info)
        // Emit custom event for App.vue to listen to
        window.dispatchEvent(
          new CustomEvent('capgo-update-available', { detail: info })
        )
      })

      // Listen for no update needed events
      CapacitorUpdater.addListener('noNeedUpdate', (info) => {
        console.log('Capgo no update needed', info)
        // Emit custom event for App.vue to listen to
        window.dispatchEvent(
          new CustomEvent('capgo-no-update', { detail: info })
        )
      })

      // Check for updates
      const { status } = await CapacitorUpdater.checkForUpdate()
      console.log('Capgo update status:', status)
    } catch (err) {
      console.error('Error initializing Capgo updater', err)
    }
  }
}

// Global function for applying updates (called from App.vue)
window.applyCapgoUpdate = async () => {
  try {
    await CapacitorUpdater.reload()
  } catch (error) {
    console.error('Failed to apply Capgo update:', error)
  }
}

// Initialize splash screen management for iOS
import { useSplashScreen } from '@/composables/useSplashScreen'

// Import route component preloader for native apps
import { preloadAllComponents } from '@/router/routeComponents'

// Don't hide splash screen immediately on iOS - let the splash manager handle it
if (Capacitor.isNativePlatform() && Capacitor.getPlatform() !== 'ios') {
  // Hide splash screen immediately for non-iOS platforms
  SplashScreen.hide()
}

router.isReady().then(async () => {
  if (Capacitor.isNativePlatform()) {
    await setupStatusBar()
    initDeeplinkListener()
    initAppLifecycleListeners()
    await initCapgoUpdater()

    // Start preloading all route components while splash is showing
    // This runs in the background and doesn't block app mounting
    preloadAllComponents().catch((err) => {
      console.error('[Main] Error preloading components:', err)
    })
  }
  app.mount('#app')
})
